import React, { useEffect, useState, FC } from 'react';
import { Carousel } from 'antd';
import _ from 'lodash';
import { withBreakpoints } from 'react-breakpoints';
import { breakpoints } from 'src/utils/breakpoints';

import styles from './Banner.module.scss';
import { ImageEntity, GlobalCta } from 'src/modules/global';

type Size = 'small' | 'large';
type DesktopAlignment = 'center' | 'right' | 'left';
type MobileAlignment = 'center' | 'top' | 'bottom';
type TextColor = 'black' | 'white';

/** GraphQL schema fields for Banner type */
export interface BannerEntity {
  mobile_images: ImageEntity[];
  desktop_images: ImageEntity[];
  headline: string;
  subline: string;
  button: { text: string; url: string; external_link: boolean };
  size: Size;
  desktop_alignment: DesktopAlignment;
  mobile_alignment: MobileAlignment;
  text_color: TextColor;
}

/** Props for Banner component */
export interface BannerProps extends BannerEntity {
  currentBreakpoint: string;
  imageTransitionSpeed: number;
}

const getContentStyle = (
  desktopAlignment: DesktopAlignment,
  mobileAlignment: MobileAlignment,
  currentBreakpoint: string,
  size: Size
) => {
  if (breakpoints[currentBreakpoint] <= breakpoints.sm) {
    // (1) small banners on small and x-small screens are always center aligned
    if (size === 'small') return styles.contentCenter;

    // (2) large banners on small and x-small screens are aligned to top or bottom
    if (mobileAlignment === 'top') return styles.contentTop;
    if (mobileAlignment === 'bottom') return styles.contentBottom;
    return styles.contentCenter;
  }

  // (3) large and small banners on m, l and xl screens are aligned left or right
  if (desktopAlignment === 'left') return styles.contentLeft;
  if (desktopAlignment === 'right') return styles.contentRight;

  return styles.contentCenter;
};

const getSublineStyle = (
  desktopAlignment: DesktopAlignment,
  textColor: TextColor,
  size: Size,
  noMarginBottom: boolean
) => {
  let sublineStyle = styles.sublineLeftOrRight;

  if (desktopAlignment === 'center' && size === 'small') {
    sublineStyle = styles.sublineCenterSmall;
  } else if (desktopAlignment === 'center' && size === 'large') {
    sublineStyle = styles.sublineCenterLarge;
  }

  if (textColor === 'white') sublineStyle = `${sublineStyle} ${styles.whiteText}`;

  if (noMarginBottom) sublineStyle = `${sublineStyle} ${styles.noMarginBottom}`;

  return sublineStyle;
};

type HeadlineProps = {
  size: Size;
  textColor: TextColor;
  desktopAlignment: DesktopAlignment;
  mobileAlignment: MobileAlignment;
  currentBreakpoint: string;
  noMarginBottom: boolean;
};
const Headline: FC<HeadlineProps> = ({
  size,
  children,
  textColor,
  desktopAlignment,
  mobileAlignment,
  currentBreakpoint,
  noMarginBottom,
}) => {
  const isMobile = breakpoints[currentBreakpoint] <= breakpoints.sm;

  let headlineStyle = styles.headlineSmall;

  if (size === 'large') {
    if (desktopAlignment === 'center' || (isMobile && mobileAlignment === 'center')) {
      headlineStyle = styles.headlineLargeCenter;
    } else {
      headlineStyle = styles.headlineLargeNotCenter;
    }
  }

  if (textColor === 'white') headlineStyle = `${headlineStyle} ${styles.whiteText}`;

  if (noMarginBottom) headlineStyle = `${headlineStyle} ${styles.noMarginBottom}`;

  return <h2 className={headlineStyle}>{children}</h2>;
};

type SublineProps = {
  show: boolean;
  desktopAlignment: DesktopAlignment;
  textColor: TextColor;
  size: Size;
  noMarginBottom: boolean;
};
const Subline: FC<SublineProps> = ({
  show,
  children,
  desktopAlignment: desktopAlignment,
  textColor,
  size,
  noMarginBottom,
}) => {
  const [sublineStyle] = useState(
    getSublineStyle(desktopAlignment, textColor, size, noMarginBottom)
  );

  if (!show) return null;

  return <p className={sublineStyle}>{children}</p>;
};

/**
 * Banner component.
 * Displays a background image, a headline text, a subline text and a button.
 */
const Banner: FC<BannerProps> = (props: BannerProps) => {
  const {
    mobile_images,
    desktop_images,
    currentBreakpoint,
    headline,
    subline,
    button,
    size = 'small',
    desktop_alignment = 'center',
    mobile_alignment = 'center',
    text_color = 'black',
    imageTransitionSpeed = 5000,
  } = props;

  const [images, setImages] = useState<null | ImageEntity[]>(null);
  const [showSubLine, setShowSubLine] = useState(true);
  const [imgStyle] = useState(size === 'small' ? styles.imgSmall : styles.imgLarge);
  const [contentStyle, setContentStyle] = useState(
    getContentStyle(desktop_alignment, mobile_alignment, currentBreakpoint, size)
  );

  useEffect(() => {
    const isMobile = breakpoints[currentBreakpoint] <= breakpoints.sm;
    setShowSubLine(!isMobile);
    setContentStyle(getContentStyle(desktop_alignment, mobile_alignment, currentBreakpoint, size));

    setImages(isMobile ? mobile_images : desktop_images);
  }, [currentBreakpoint, desktop_alignment, desktop_images, mobile_alignment, mobile_images, size]);

  if (!images) return null;
  const noButton = !button;

  return (
    <Carousel autoplay autoplaySpeed={imageTransitionSpeed} dots={false} effect="fade">
      {_.map(images, (img, id) => (
        <div key={id}>
          <div className={imgStyle} style={{ backgroundImage: `url(${img.url})` }}>
            <div className={contentStyle}>
              <Headline
                size={size}
                textColor={text_color}
                desktopAlignment={desktop_alignment}
                mobileAlignment={mobile_alignment}
                noMarginBottom={!showSubLine && noButton}
                currentBreakpoint={currentBreakpoint}>
                {headline}
              </Headline>
              <Subline
                size={size}
                show={showSubLine}
                desktopAlignment={desktop_alignment}
                noMarginBottom={noButton}
                textColor={text_color}>
                {subline}
              </Subline>
              {!!button && (
                <GlobalCta
                  {...button}
                  isButton={true}
                  overlay={null}
                  download={null}
                  type={text_color === 'white' ? 'white' : 'primary'}
                  widthType="padded"
                />
              )}
            </div>
          </div>
        </div>
      ))}
    </Carousel>
  );
};

export default withBreakpoints(Banner);
